import { useState, useMemo } from "react";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths, subWeeks, differenceInMonths } from "date-fns";
import { CalendarIcon, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const PRESETS = [
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "last_3_months", label: "Last 3 Months" },
  { value: "custom", label: "Custom" },
];

function getPresetRange(preset) {
  const today = new Date();
  switch (preset) {
    case "yesterday":
      const yday = subDays(today, 1);
      return { start: yday, end: yday };
    case "this_week":
      return { start: startOfWeek(today, { weekStartsOn: 1 }), end: today };
    case "last_week": {
      const lw = subWeeks(today, 1);
      return { start: startOfWeek(lw, { weekStartsOn: 1 }), end: endOfWeek(lw, { weekStartsOn: 1 }) };
    }
    case "this_month":
      return { start: startOfMonth(today), end: today };
    case "last_month": {
      const lm = subMonths(today, 1);
      return { start: startOfMonth(lm), end: endOfMonth(lm) };
    }
    case "last_3_months":
      return { start: startOfMonth(subMonths(today, 2)), end: today };
    default:
      return null;
  }
}

export function DateRangePicker({ startDate, endDate, onStartChange, onEndChange, availableDates, isAdmin }) {
  const [preset, setPreset] = useState("this_month");
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  // Dates are initialized by the parent component - no mount effect needed

  const handlePresetChange = (value) => {
    setPreset(value);
    if (value === "custom") return;
    const range = getPresetRange(value);
    if (range) {
      onStartChange(range.start);
      onEndChange(range.end);
    }
  };

  const handleStartChange = (d) => {
    if (!isAdmin && endDate) {
      const months = differenceInMonths(endDate, d);
      if (months > 12) {
        toast.error("Maximum 12-month date range allowed");
        return;
      }
    }
    onStartChange(d);
    setStartOpen(false);
  };

  const handleEndChange = (d) => {
    if (!isAdmin && startDate) {
      const months = differenceInMonths(d, startDate);
      if (months > 12) {
        toast.error("Maximum 12-month date range allowed");
        return;
      }
    }
    onEndChange(d);
    setEndOpen(false);
  };

  const availableSet = useMemo(() => new Set(availableDates || []), [availableDates]);

  const disabledMatcher = availableDates && availableDates.length > 0
    ? (date) => !availableSet.has(format(date, "yyyy-MM-dd"))
    : undefined;

  const showCalendars = preset === "custom";

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="date-range-picker">
      <Select value={preset} onValueChange={handlePresetChange}>
        <SelectTrigger className="w-[160px] h-9 border-slate-200 text-sm" data-testid="date-preset-select">
          <SelectValue placeholder="Select range" />
        </SelectTrigger>
        <SelectContent>
          {PRESETS.map((p) => (
            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {showCalendars && (
        <>
          <Popover open={startOpen} onOpenChange={setStartOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                data-testid="start-date-trigger"
                className="h-9 px-3 text-sm border-slate-200 text-slate-700 hover:bg-slate-50 font-normal justify-start"
              >
                <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                {startDate ? format(startDate, "MMM dd, yyyy") : "Start date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={handleStartChange}
                disabled={disabledMatcher}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <span className="text-slate-400 text-sm">to</span>
          <Popover open={endOpen} onOpenChange={setEndOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                data-testid="end-date-trigger"
                className="h-9 px-3 text-sm border-slate-200 text-slate-700 hover:bg-slate-50 font-normal justify-start"
              >
                <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                {endDate ? format(endDate, "MMM dd, yyyy") : "End date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={handleEndChange}
                disabled={disabledMatcher}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </>
      )}

      {!showCalendars && startDate && endDate && (
        <span className="text-xs text-slate-500 font-medium" data-testid="date-range-display">
          {format(startDate, "MMM dd")} — {format(endDate, "MMM dd, yyyy")}
        </span>
      )}
    </div>
  );
}
